import { QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // The websocket (hooks/useLiveEvents.ts) is what keeps data fresh; polling on top of
      // that would just spend battery re-asking questions the socket already answers.
      staleTime: 15_000,
      retry: 1,
    },
  },
});

/** Query key roots shared between the fetchers and the live-event invalidation map. */
export const queryKeys = {
  health: ["health"] as const,
  signals: (filters: Record<string, unknown> = {}) => ["signals", filters] as const,
  signal: (id: number) => ["signal", id] as const,
  orders: (filters: Record<string, unknown> = {}) => ["orders", filters] as const,
  positions: ["positions"] as const,
  account: ["account"] as const,
  decisions: (filters: Record<string, unknown> = {}) => ["decisions", filters] as const,
  channels: ["channels"] as const,
  accounts: ["accounts"] as const,
  telegramDialogs: ["telegramDialogs"] as const,
  // The broker's instrument list only changes on a spec resync; cache it generously.
  symbols: ["symbols"] as const,
  credits: ["credits"] as const,
  creditsHistory: (days: number) => ["creditsHistory", days] as const,
  // Nested under "channels" so a live "channels" invalidation (see useLiveEvents.ts's
  // ALL_ROOTS/applyLiveEvent) matches this key by prefix without needing its own root.
  channelRules: (id: number) => ["channels", id, "rules"] as const,
  channelStats: (id: number, params: Record<string, unknown> = {}) =>
    ["channels", id, "stats", params] as const,
  channelComparison: (days: number) => ["stats", "channels", days] as const,
  notifications: (params: Record<string, unknown> = {}) => ["notifications", "list", params] as const,
  currency: ["currency"],
  equity: (days = 30) => ["equity", days] as const,
  pushConfig: ["push", "config"] as const,
  unreadNotifications: ["notifications", "unread"] as const,
  statsOverview: (period: string) => ["stats", "overview", period] as const,
  report: (period: string, offset: number) => ["stats", "report", period, offset] as const,
  globalRules: ["settings", "rules"] as const,
  settings: ["settings"] as const,
  // M41 — one root, so a control's invalidation and a socket reconnect refresh both.
  selfTraderStatus: ["selfTrader", "status"] as const,
  selfTraderPortfolios: ["selfTrader", "portfolios"] as const,
};
